import React, { useEffect, useState, useRef, useMemo } from "react";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue, SelectGroup, SelectLabel } from "@/components/ui/select";
import { toast } from "sonner";
import { formatApiError, formatIndian, purityColor } from "@/lib/utils";
import { UploadCloud, CheckCircle2, AlertCircle, X, Copy, FileDown, Check, ArrowRightCircle } from "lucide-react";

export default function UploadPage() {
  const [franchises, setFranchises] = useState([]);
  const [distributors, setDistributors] = useState([]);
  const [shapes, setShapes] = useState([]);
  const [sourceTag, setSourceTag] = useState("");
  const [newTag, setNewTag] = useState("");
  const [file, setFile] = useState(null);
  const [preview, setPreview] = useState(null);
  const [result, setResult] = useState(null);
  const [busy, setBusy] = useState(false);
  const [copied, setCopied] = useState(false);
  const [markMemo, setMarkMemo] = useState(false);
  // raw_shape (normalized lower-case, trimmed) → short_code chosen by admin
  const [shapeMap, setShapeMap] = useState({});
  // raw_shape (normalized) → boolean "remember"
  const [rememberMap, setRememberMap] = useState({});
  // raw_shape (normalized) → original raw display name (captured when shape first appears in errors).
  // We persist this across preview re-runs so the mapping survives even after error_rows clears.
  const [rawByKey, setRawByKey] = useState({});
  const inputRef = useRef();

  useEffect(() => {
    api.get("/franchises").then((r) => setFranchises(r.data)).catch(() => {});
    api.get("/distributors").then((r) => setDistributors(r.data)).catch(() => {});
    api.get("/diamond-shapes").then((r) => setShapes(r.data.filter((s) => s.is_active))).catch(() => {});
  }, []);

  const handleFile = (f) => {
    if (!f) return;
    setFile(f);
    setPreview(null);
    setResult(null);
    setShapeMap({});
    setRememberMap({});
    setRawByKey({});
  };

  // Parse composite "type:id" value back into {source_type, source_id, source_name}
  const parseSource = (composite) => {
    if (!composite || composite === "__new__") return null;
    const [type, id] = composite.split(":");
    if (type === "ho") return { source_type: "ho", source_id: null, source_name: "Head Office Stock" };
    if (type === "unassigned") return { source_type: "unassigned", source_id: null, source_name: "Unassigned" };
    if (type === "franchise") {
      const f = franchises.find((x) => x.id === id);
      return f ? { source_type: "franchise", source_id: id, source_name: f.name } : null;
    }
    if (type === "distributor") {
      const d = distributors.find((x) => x.id === id);
      return d ? { source_type: "distributor", source_id: id, source_name: d.name } : null;
    }
    return null;
  };
  const normKey = (s) => String(s || "").trim().toLowerCase();

  // Collect unique unrecognised raw shape names across all error rows
  const uniqueUnknownShapes = useMemo(() => {
    if (!preview?.error_rows) return [];
    const seen = new Map(); // normalized → display raw
    for (const er of preview.error_rows) {
      for (const raw of (er.unrecognised_shapes || [])) {
        const k = normKey(raw);
        if (k && !seen.has(k)) seen.set(k, raw);
      }
    }
    return Array.from(seen.entries()).map(([k, raw]) => ({ key: k, raw }));
  }, [preview]);

  // Merge any newly-seen unknown shapes into the persistent rawByKey map so
  // override payloads survive even after "Resolve & Move" clears error_rows.
  useEffect(() => {
    if (uniqueUnknownShapes.length === 0) return;
    setRawByKey((prev) => {
      const next = { ...prev };
      let changed = false;
      for (const { key, raw } of uniqueUnknownShapes) {
        if (!next[key]) { next[key] = raw; changed = true; }
      }
      return changed ? next : prev;
    });
  }, [uniqueUnknownShapes]);

  // Build the {raw: short_code} object for the request — iterate the persisted
  // shapeMap (not the live error_rows) so mappings survive Resolve & Move.
  const buildOverrides = () => {
    const out = {};
    for (const [key, code] of Object.entries(shapeMap)) {
      const raw = rawByKey[key];
      if (raw && code) out[raw] = code;
    }
    return out;
  };

  const allUnknownsMapped = uniqueUnknownShapes.length > 0 &&
    uniqueUnknownShapes.every(({ key }) => !!shapeMap[key]);

  const doPreview = async (overridesObj = null) => {
    const src = parseSource(sourceTag);
    const newName = (sourceTag === "__new__" ? newTag.trim() : null);
    if (!file) return toast.error("Choose an Excel file");
    if (!src && !newName) return toast.error("Source is required");
    setBusy(true);
    setResult(null);
    try {
      const fd = new FormData();
      fd.append("file", file);
      if (src) {
        fd.append("source_type", src.source_type);
        if (src.source_id) fd.append("source_id", src.source_id);
      } else if (newName) {
        // Free-text new source — backend treats as unassigned with the given name
        fd.append("source_tag", newName);
      }
      if (overridesObj && Object.keys(overridesObj).length) {
        fd.append("shape_overrides", JSON.stringify(overridesObj));
      }
      if (markMemo) fd.append("is_memo", "true");
      const { data } = await api.post("/products/preview-upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setPreview(data);
      if (data.errors?.length) {
        toast.error(`Cannot parse: ${data.errors[0]}`);
      } else {
        const valid = data.products?.length || 0;
        const errs = data.error_rows?.length || 0;
        if (errs > 0) toast.message(`${valid} valid, ${errs} with errors`);
        else toast.success(`Parsed ${valid} products`);
      }
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Preview failed");
    } finally {
      setBusy(false);
    }
  };

  const resolveAndMove = async () => {
    if (!allUnknownsMapped) {
      return toast.error("Pick a shape for every unrecognised name first");
    }
    await doPreview(buildOverrides());
    toast.success("Mappings applied — rows moved to the upload queue");
  };

  const doUpload = async ({ skipErrors = false } = {}) => {
    const src = parseSource(sourceTag);
    const newName = (sourceTag === "__new__" ? newTag.trim() : null);
    if (!file) return toast.error("Choose an Excel file");
    if (!src && !newName) return toast.error("Source is required");
    setBusy(true);
    try {
      const overrides = buildOverrides();
      const willRemember = Object.entries(rememberMap)
        .some(([k, v]) => v && shapeMap[k]);
      const fd = new FormData();
      fd.append("file", file);
      if (src) {
        fd.append("source_type", src.source_type);
        if (src.source_id) fd.append("source_id", src.source_id);
      } else if (newName) {
        fd.append("source_tag", newName);
      }
      fd.append("skip_errors", skipErrors ? "true" : "false");
      fd.append("is_memo", markMemo ? "true" : "false");
      if (Object.keys(overrides).length) {
        fd.append("shape_overrides", JSON.stringify(overrides));
        fd.append("remember_aliases", willRemember ? "true" : "false");
      }
      const { data } = await api.post("/products/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setResult(data);
      if (data.ok) {
        let msg = `${data.added} added · ${data.updated} updated`;
        if (data.memo_count) msg += ` · ${data.memo_count} marked as MEMO`;
        if (data.skipped) msg += ` · ${data.skipped} skipped`;
        if (data.persisted_aliases?.length) msg += ` · ${data.persisted_aliases.length} alias${data.persisted_aliases.length === 1 ? "" : "es"} saved`;
        toast.success(msg);
        // Refresh shapes list so newly saved aliases appear in future uploads
        if (data.persisted_aliases?.length) {
          api.get("/diamond-shapes").then((r) => setShapes(r.data.filter((s) => s.is_active))).catch(() => {});
        }
      } else {
        toast.error((data.errors && data.errors[0]) || "Upload failed");
      }
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Upload failed");
    } finally {
      setBusy(false);
    }
  };

  const errorRows = preview?.error_rows || [];
  const validProducts = preview?.products || [];

  const buildErrorText = () =>
    ["Row\tJewel Code\tReason", ...errorRows.map((r) => `${r.row}\t${r.jewel_code}\t${r.reason}`)].join("\n");

  const copyErrors = async () => {
    try {
      await navigator.clipboard.writeText(buildErrorText());
      setCopied(true);
      toast.success("Error list copied");
      setTimeout(() => setCopied(false), 2000);
    } catch {
      toast.error("Clipboard blocked — use Download instead");
    }
  };

  const downloadErrorReport = async () => {
    const src = parseSource(sourceTag);
    const newName = (sourceTag === "__new__" ? newTag.trim() : null);
    if (!file || (!src && !newName)) return;
    try {
      const fd = new FormData();
      fd.append("file", file);
      if (src) {
        fd.append("source_type", src.source_type);
        if (src.source_id) fd.append("source_id", src.source_id);
      } else if (newName) {
        fd.append("source_tag", newName);
      }
      const tok = localStorage.getItem("access_token");
      const res = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/products/upload-error-report`, {
        method: "POST",
        headers: tok ? { Authorization: `Bearer ${tok}` } : {},
        body: fd,
      });
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = "upload_errors.xlsx";
      a.click();
      URL.revokeObjectURL(url);
    } catch (e) {
      toast.error("Download failed");
    }
  };

  return (
    <div className="space-y-8" data-testid="upload-page">
      <div>
        <p className="text-xs uppercase tracking-[0.25em] text-[#C9A84C] mb-2">Catalogue</p>
        <h1 className="font-serif text-4xl md:text-5xl text-[#0D1B2A]">Upload Products</h1>
        <p className="text-slate-500 mt-2">Drop an Excel file to ingest products with full pricing computed on the fly.</p>
      </div>

      <div className="luxe-card p-8 space-y-6">
        <div className="grid md:grid-cols-2 gap-6">
          <div>
            <label className="text-xs uppercase tracking-widest text-slate-500 mb-2 block">Source Tag</label>
            <Select value={sourceTag} onValueChange={setSourceTag}>
              <SelectTrigger className="h-11 bg-white border-[#0D1B2A]/15" data-testid="source-select"><SelectValue placeholder="Choose a source…" /></SelectTrigger>
              <SelectContent>
                <SelectGroup>
                  <SelectLabel className="text-[10px] uppercase tracking-wider text-slate-400">Head Office</SelectLabel>
                  <SelectItem value="ho:none">Head Office Stock</SelectItem>
                </SelectGroup>
                <SelectGroup>
                  <SelectLabel className="text-[10px] uppercase tracking-wider text-slate-400">Franchises</SelectLabel>
                  {franchises.map((f) => <SelectItem key={`f-${f.id}`} value={`franchise:${f.id}`}>{f.name}</SelectItem>)}
                  {franchises.length === 0 && <div className="px-2 py-1.5 text-xs text-slate-400 italic">No franchises</div>}
                </SelectGroup>
                {/* CS 21.19 -- Distributors REMOVED from the catalogue source picker.
                    Distributor uploads live on the Distributors page (Upload Products
                    action) and target distributor_stock, not the shared products
                    catalog. Do NOT re-add a Distributors group here. */}
                <SelectGroup>
                  <SelectLabel className="text-[10px] uppercase tracking-wider text-slate-400">Other</SelectLabel>
                  <SelectItem value="unassigned:none">Unassigned</SelectItem>
                  <SelectItem value="__new__">+ New source…</SelectItem>
                </SelectGroup>
              </SelectContent>
            </Select>
            {sourceTag === "__new__" && (
              <Input className="mt-3 h-11" value={newTag} onChange={(e) => setNewTag(e.target.value)} placeholder="New source name" data-testid="new-source-input" />
            )}
          </div>
          <div>
            <label className="text-xs uppercase tracking-widest text-slate-500 mb-2 block">Excel File</label>
            <div
              onDragOver={(e) => e.preventDefault()}
              onDrop={(e) => { e.preventDefault(); handleFile(e.dataTransfer.files[0]); }}
              onClick={() => inputRef.current?.click()}
              className="border-2 border-dashed border-[#0D1B2A]/15 rounded-md p-6 text-center cursor-pointer hover:border-[#C9A84C] transition-colors"
              data-testid="upload-dropzone"
            >
              <UploadCloud className="w-8 h-8 mx-auto text-[#C9A84C] mb-2" strokeWidth={1.5} />
              <p className="text-sm text-slate-600">{file ? file.name : "Click to choose or drag .xlsx here"}</p>
              <input ref={inputRef} type="file" accept=".xlsx,.xls" className="hidden" onChange={(e) => handleFile(e.target.files[0])} data-testid="upload-file-input" />
            </div>
          </div>
        </div>
        <div className="flex items-start gap-3 p-3 rounded-md border border-[#0D1B2A]/10 bg-[#FAF8F3]" data-testid="memo-toggle-row">
          <label className="flex items-start gap-3 cursor-pointer select-none flex-1">
            <input
              type="checkbox"
              checked={markMemo}
              onChange={(e) => setMarkMemo(e.target.checked)}
              data-testid="memo-toggle"
              className="mt-1 w-4 h-4 rounded border-[#0D1B2A]/30 text-[#16A34A] focus:ring-[#16A34A]"
            />
            <div>
              <div className="text-sm font-medium text-[#0D1B2A]">Mark all products in this upload as MEMO (consignment)</div>
              <p className="text-xs text-slate-500 mt-1">Memo products show a green MEMO badge. Re-uploading the same Jewel Code without this toggle will automatically remove the memo status.</p>
            </div>
          </label>
        </div>
        <div className="flex flex-wrap gap-3">
          <Button onClick={() => doPreview()} disabled={busy || !file} className="bg-[#0D1B2A] text-white hover:bg-[#0D1B2A]/90" data-testid="upload-preview-btn">
            Preview parse
          </Button>
          {(!preview || errorRows.length === 0) && (
            <Button onClick={() => doUpload({ skipErrors: false })} disabled={busy || !file || (preview?.errors?.length > 0)} className="bg-[#C9A84C] hover:bg-[#C9A84C]/90 text-white" data-testid="upload-confirm-btn">
              Confirm upload
            </Button>
          )}
        </div>
      </div>

      {preview?.errors?.length > 0 && (
        <div className="luxe-card p-6 border-l-4 border-red-500" data-testid="upload-fatal-errors">
          <div className="flex items-center gap-2 text-red-800 font-medium mb-2">
            <AlertCircle className="w-4 h-4" /> Cannot parse file
          </div>
          <ul className="text-sm text-red-700 list-disc pl-5">
            {preview.errors.map((e, i) => <li key={`pe-${i}-${String(e).slice(0,40)}`}>{e}</li>)}
          </ul>
        </div>
      )}

      {preview && preview.errors?.length === 0 && (
        <div className="grid lg:grid-cols-2 gap-6">
          {/* Valid */}
          <div className="luxe-card p-6" data-testid="valid-section">
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center gap-2">
                <CheckCircle2 className="w-5 h-5 text-emerald-600" />
                <h2 className="font-serif text-xl text-[#0D1B2A]">Ready to upload</h2>
                <span className="text-sm font-mono bg-emerald-100 text-emerald-700 px-2 py-0.5 rounded" data-testid="valid-count">{validProducts.length}</span>
              </div>
            </div>
            {validProducts.length === 0 ? (
              <p className="text-sm text-slate-400">No valid rows.</p>
            ) : (
              <div className="overflow-x-auto max-h-[420px] overflow-y-auto">
                <table className="w-full text-sm">
                  <thead className="sticky top-0 bg-white">
                    <tr className="text-left text-xs uppercase tracking-wider text-slate-500 border-b border-[#0D1B2A]/10">
                      <th className="py-2 pr-3">Jewel</th><th className="py-2 pr-3">Cat</th>
                      <th className="py-2 pr-3">Purity</th><th className="py-2 pr-3 text-right">Net Wt</th><th className="py-2 pr-3 text-right">Lines</th>
                    </tr>
                  </thead>
                  <tbody>
                    {validProducts.map((p) => (
                      <tr key={p.jewel_code} className="border-b border-[#0D1B2A]/5">
                        <td className="py-2 pr-3 font-medium">{p.jewel_code}</td>
                        <td className="py-2 pr-3">{p.category}</td>
                        <td className="py-2 pr-3"><span className={`text-[10px] uppercase tracking-wider px-2 py-0.5 rounded-full border ${purityColor(p.purity)}`}>{p.purity}</span></td>
                        <td className="py-2 pr-3 text-right tabular-nums">{formatIndian(p.net_weight, { decimals: 3 })} g</td>
                        <td className="py-2 pr-3 text-right tabular-nums">{p.diamond_lines.length}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>

          {/* Errors */}
          <div className="luxe-card p-6" data-testid="errors-section">
            <div className="flex items-center justify-between mb-4 gap-2 flex-wrap">
              <div className="flex items-center gap-2">
                <AlertCircle className="w-5 h-5 text-red-500" />
                <h2 className="font-serif text-xl text-[#0D1B2A]">Errors found</h2>
                <span className="text-sm font-mono bg-red-100 text-red-700 px-2 py-0.5 rounded" data-testid="error-count">{errorRows.length}</span>
              </div>
              {errorRows.length > 0 && (
                <div className="flex gap-2">
                  <button onClick={copyErrors} className="px-3 py-1.5 rounded-md border border-[#0D1B2A]/15 text-xs flex items-center gap-1.5 hover:bg-[#FAF8F3]" data-testid="copy-errors-btn">
                    {copied ? <><Check className="w-3.5 h-3.5" /> Copied</> : <><Copy className="w-3.5 h-3.5" /> Copy list</>}
                  </button>
                  <button onClick={downloadErrorReport} className="px-3 py-1.5 rounded-md border border-[#0D1B2A]/15 text-xs flex items-center gap-1.5 hover:bg-[#FAF8F3]" data-testid="download-errors-btn">
                    <FileDown className="w-3.5 h-3.5" /> Download
                  </button>
                </div>
              )}
            </div>

            {/* Unique unknown shape mapping UI */}
            {uniqueUnknownShapes.length > 0 && (
              <div className="mb-5 p-4 rounded-md border border-amber-200 bg-amber-50/60" data-testid="shape-mapping-panel">
                <div className="text-xs font-medium text-amber-900 mb-3 flex items-center gap-1.5">
                  <AlertCircle className="w-3.5 h-3.5" />
                  Map {uniqueUnknownShapes.length} unrecognised shape{uniqueUnknownShapes.length === 1 ? "" : "s"} to upload these rows
                </div>
                <div className="space-y-2">
                  {uniqueUnknownShapes.map(({ key, raw }) => (
                    <div key={key} className="grid grid-cols-12 gap-2 items-center" data-testid={`map-row-${key}`}>
                      <div className="col-span-4 text-sm font-mono text-amber-900 truncate" title={raw}>{raw}</div>
                      <div className="col-span-1 text-center text-amber-400">→</div>
                      <div className="col-span-4">
                        <Select value={shapeMap[key] || ""} onValueChange={(v) => setShapeMap({ ...shapeMap, [key]: v })}>
                          <SelectTrigger className="h-9 bg-white" data-testid={`map-select-${key}`}>
                            <SelectValue placeholder={`Select rate table for '${raw}'`} />
                          </SelectTrigger>
                          <SelectContent>
                            {shapes.map((s) => (
                              <SelectItem key={s.short_code} value={s.short_code}>
                                {s.short_code} — {s.display_name}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                      <label className="col-span-3 text-[11px] text-amber-800 flex items-center gap-1.5 cursor-pointer select-none">
                        <input
                          type="checkbox"
                          checked={!!rememberMap[key]}
                          onChange={(e) => setRememberMap({ ...rememberMap, [key]: e.target.checked })}
                          className="rounded border-amber-400"
                          data-testid={`map-remember-${key}`}
                        />
                        Remember this mapping
                      </label>
                    </div>
                  ))}
                </div>
                <div className="mt-3 flex justify-end">
                  <Button
                    onClick={resolveAndMove}
                    disabled={busy || !allUnknownsMapped}
                    className="bg-amber-600 hover:bg-amber-700 text-white disabled:opacity-50"
                    data-testid="resolve-mappings-btn"
                  >
                    <ArrowRightCircle className="w-4 h-4 mr-2" /> Resolve & Move to Upload Queue
                  </Button>
                </div>
              </div>
            )}

            {errorRows.length === 0 ? (
              <p className="text-sm text-emerald-600">No errors — all rows are valid.</p>
            ) : (
              <div className="overflow-x-auto max-h-[420px] overflow-y-auto">
                <table className="w-full text-sm">
                  <thead className="sticky top-0 bg-white">
                    <tr className="text-left text-xs uppercase tracking-wider text-slate-500 border-b border-[#0D1B2A]/10">
                      <th className="py-2 pr-3">Row</th><th className="py-2 pr-3">Jewel Code</th><th className="py-2 pr-3">Reason</th>
                    </tr>
                  </thead>
                  <tbody>
                    {errorRows.map((r, i) => (
                      <tr key={`err-${r.row}-${r.jewel_code}-${i}`} className="border-b border-[#0D1B2A]/5">
                        <td className="py-2 pr-3 font-mono text-slate-400">{r.row}</td>
                        <td className="py-2 pr-3 font-medium">{r.jewel_code}</td>
                        <td className="py-2 pr-3 text-red-700">{r.reason}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>

          {validProducts.length > 0 && (
            <div className="lg:col-span-2 flex justify-end">
              <Button
                onClick={() => doUpload({ skipErrors: errorRows.length > 0 })}
                disabled={busy}
                className="bg-[#C9A84C] hover:bg-[#C9A84C]/90 text-white"
                data-testid="upload-valid-only-btn"
              >
                Upload {validProducts.length} valid product{validProducts.length === 1 ? "" : "s"}{errorRows.length > 0 ? ` (skip ${errorRows.length} errors)` : ""}
              </Button>
            </div>
          )}
        </div>
      )}

      {result && (
        <div className={`luxe-card p-6 border-l-4 ${result.ok ? "border-emerald-500" : "border-red-500"}`} data-testid="upload-result">
          {result.ok ? (
            <div className="space-y-3">
              <div className="flex items-center gap-3 text-emerald-700">
                <CheckCircle2 className="w-5 h-5" />
                <div>
                  {result.added} added · {result.updated} updated{result.skipped ? ` · ${result.skipped} skipped` : ""}
                </div>
              </div>
              {result.persisted_aliases?.length > 0 && (
                <div className="text-xs text-slate-500" data-testid="persisted-aliases">
                  Saved as aliases: {result.persisted_aliases.map((a) => `"${a.raw}" → ${a.short_code}`).join(", ")}
                </div>
              )}
            </div>
          ) : (
            <div className="flex items-center gap-3 text-red-700">
              <X className="w-5 h-5" />
              <div>{(result.errors && result.errors[0]) || "Upload failed"}</div>
            </div>
          )}
        </div>
      )}

      <div className="luxe-card p-6">
        <h3 className="font-serif text-xl text-[#0D1B2A] mb-3">Expected Excel format</h3>
        <p className="text-sm text-slate-500 mb-3">Header row must contain: Sr No · Style No · JewelCode · Category · Sub Category · Item Code · Gross Wt · Net Wt · Shape · Pcs · Wt</p>
        <p className="text-xs text-slate-400 mb-3">Continuation rows (no Sr No) may carry additional metal components or extra diamond lines for the same product. The Shape column accepts either the short code (e.g. <span className="font-mono">RD</span>) or the full display name (case-insensitive). Duplicate Jewel Codes overwrite existing records.</p>
        <div className="text-xs text-slate-500 bg-[#b8893b]/8 border border-[#b8893b]/25 rounded p-3 space-y-2">
          <div>
            <span className="font-semibold text-[#b8893b]">Optional column — Distribution Catalog:</span> add a column named “Distribution Catalog” and set the cell to <span className="font-mono">Y</span> (or Yes / true / 1) to map that product into the Distribution Master Catalog at upload time. Leave blank or set <span className="font-mono">N</span> to keep it out. This flag can also be toggled later from the Products page (per-row or in bulk).
          </div>
          <div>
            <span className="font-semibold text-[#b8893b]">Optional column — Base Purity:</span> add a column named “Base Purity” with one of <span className="font-mono">9kt</span> / <span className="font-mono">14kt</span> / <span className="font-mono">18kt</span> to declare the purity at which the Net Wt is expressed. Distribution links automatically convert this weight to other purities using the weight-index table. Defaults to <span className="font-mono">18kt</span> if omitted.
          </div>
        </div>
      </div>
    </div>
  );
}
